import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Combine clsx and tailwind-merge for efficient Tailwind class merging
 */
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Format a number as USD currency string
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Parse a Decimal string from backend to number
 * Backend sends Decimals as strings for precision
 */
export function parseDecimal(value: string | number): number {
  if (typeof value === 'number') return value
  const parsed = parseFloat(value)
  if (isNaN(parsed)) {
    throw new Error(`Invalid decimal value: ${value}`)
  }
  return parsed
}

/**
 * Format a number to a specific decimal precision for display
 */
export function formatDecimal(value: number, decimals: number = 2): string {
  return value.toFixed(decimals)
}

/**
 * Format a percentage for display
 */
export function formatPercentage(value: number, decimals: number = 2): string {
  const sign = value >= 0 ? '+' : ''
  return `${sign}${value.toFixed(decimals)}%`
}

/**
 * Safe number formatter that handles null/undefined
 */
export function safeFormatNumber(
  value: string | number | null | undefined,
  formatter: (n: number) => string
): string {
  if (value === null || value === undefined) return '-'
  try {
    const num = parseDecimal(value)
    return formatter(num)
  } catch {
    return '-'
  }
}
